import type { z } from "zod";

export { generateCacheKey } from "../utils/hashString";

const CACHE_NAME = "travel-journal-responses-v1";
export const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export type CachedResponseResult<T> =
  | { status: "hit"; data: T }
  | { status: "miss" | "expired" | "invalid" };

export async function getCache(): Promise<Cache | null> {
  if (!("caches" in window)) {
    return null;
  }

  try {
    return await caches.open(CACHE_NAME);
  } catch (error) {
    console.debug("Cache API is unavailable:", error);
    return null;
  }
}

export function createCacheRequest(cacheKey: string): Request {
  return new Request(`https://api.local/cache/${cacheKey}`, {
    method: "GET",
    headers: {
      "Cache-Key": cacheKey,
    },
  });
}

export async function parseCachedResponse<T>(
  response: Response | undefined,
  responseSchema: z.ZodType<T>,
  now = Date.now(),
): Promise<CachedResponseResult<T>> {
  if (!response) {
    return { status: "miss" };
  }

  const cachedAt = Date.parse(response.headers.get("Cached-At") ?? "");
  if (!Number.isFinite(cachedAt) || now - cachedAt > CACHE_TTL_MS) {
    return { status: "expired" };
  }

  try {
    const parsed = responseSchema.safeParse(await response.json());
    return parsed.success ? { status: "hit", data: parsed.data } : { status: "invalid" };
  } catch {
    return { status: "invalid" };
  }
}
